import React from 'react';
import { Spin } from 'antd';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

/**
 * 统一指标图表组件（纯展示，数据由父组件加载）
 *
 * @param {Object} props
 * @param {Object|null} props.chartData - { labels: string[], values: number[] } 或 null
 * @param {Object} props.indicator - 指标配置对象 ({ key, label, unit })
 * @param {Object} [props.indicatorTargets] - { [indicatorKey]: { targetYear, target }[] }
 * @param {boolean} [props.loading=false] - 加载中状态
 */
const IndicatorChart = ({ chartData, indicator, indicatorTargets, loading }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!chartData || !chartData.labels || chartData.labels.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
        <p>暂无数据</p>
      </div>
    );
  }

  const targets = indicatorTargets?.[indicator.key] || [];
  const sortedTargets = [...targets].sort((a, b) => a.targetYear - b.targetYear);

  const datasets = [
    {
      label: indicator.label,
      data: chartData.values,
      borderColor: 'rgb(75, 192, 192)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      tension: 0.1,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBackgroundColor: 'rgb(75, 192, 192)',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      spanGaps: false,
    },
  ];

  if (sortedTargets.length > 0) {
    const targetData = chartData.labels.map((yearStr) => {
      const year = typeof yearStr === 'string'
        ? parseInt(yearStr.replace('年', ''), 10)
        : parseInt(yearStr, 10);
      const targetForYear = sortedTargets
        .filter((t) => year >= t.targetYear)
        .sort((a, b) => b.targetYear - a.targetYear)[0];
      return targetForYear ? targetForYear.target : null;
    });

    datasets.push({
      label: '目标',
      data: targetData,
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: 'rgb(255, 99, 132)',
      fill: false,
      spanGaps: false,
    });
  }

  const chartConfig = {
    labels: chartData.labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 0,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: indicator.label,
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title(context) {
            if (context && context.length > 0) {
              const label = context[0].label || '';
              if (typeof label === 'string' && label.includes('年')) {
                return label;
              }
              const yearMatch = String(label).match(/\d{4}/);
              if (yearMatch) {
                return `${yearMatch[0]}年`;
              }
              return `${label}年`;
            }
            return '';
          },
          label(context) {
            try {
              if (!context || !context.parsed) return '';
              const value = context.parsed.y ?? context.parsed;
              if (value === null || value === undefined || isNaN(value)) return '';
              const label = context.dataset?.label || '';
              const unit = indicator.unit || '';
              return `${label}: ${value.toFixed(2)}${unit ? ` ${unit}` : ''}`;
            } catch {
              return '';
            }
          },
          filter(tooltipItem) {
            try {
              if (!tooltipItem || !tooltipItem.parsed) return false;
              const value = tooltipItem.parsed?.y ?? tooltipItem.parsed;
              return value !== null && value !== undefined && !isNaN(value);
            } catch {
              return false;
            }
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'top',
        formatter: (value) => {
          return value !== null && value !== undefined ? value.toFixed(2) : '';
        },
        color: '#333',
        font: {
          weight: 'bold',
          size: 11,
        },
        offset: 8,
        clamp: true,
        clip: false,
        padding: {
          top: 2,
          bottom: 2,
          left: 2,
          right: 2,
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: '年份',
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: indicator.unit || '数值',
        },
        ticks: {
          precision: 2,
        },
      },
    },
  };

  return (
    <div style={{ height: '400px', width: '100%', margin: 0, padding: 0 }}>
      <Line data={chartConfig} options={options} plugins={[ChartDataLabels]} />
    </div>
  );
};

export default IndicatorChart;
